import { useEffect, useReducer } from 'react';
import useWebsocket from 'react-use-websocket';
import ServicePaths from '../bridge/utils/ServicePaths';
import {
  CPCLiveMessageType,
  type CPCProgressMessage,
  type CPCPhase,
} from '../bridge/utils/CPCLiveMessages';
import type {
  BestCalculationResult,
  ResultOverviewFile,
} from 'grts-utils/types/ProfitCalcs';
import type { TimeFrame } from 'grts-utils/types/TimeFrame';

export type ProgressState = {
  runId: string | null;
  isIdle: boolean;
  isFinished: boolean;
  finishedDuration: number | null;
  baseAsset: string | null;
  quoteAsset: string | null;
  comboIndex: number;
  comboTotal: number;
  currentCombo: {
    higherTimeFrame: TimeFrame;
    shorterTimeFrame: TimeFrame;
    multiOrderTimeFrame: TimeFrame;
    startedAt: number;
  } | null;
  phase: CPCPhase | null;
  phaseTotalIterations: number;
  phaseDone: number;
  phaseStartedAt: number | null;
  phasePass: number | null;
  overview: ResultOverviewFile | null;
  bestGlobal: BestCalculationResult | null;
  startedAt: number | null;
  finishedDurations: number[];
  interruptedReason: string | null;
};

// Empty per-asset state. Exported so consumers can render a placeholder for an
// asset that is active in the queue but hasn't streamed its first message yet.
export const EMPTY_PROGRESS: ProgressState = {
  runId: null,
  isIdle: true,
  isFinished: false,
  finishedDuration: null,
  baseAsset: null,
  quoteAsset: null,
  comboIndex: 0,
  comboTotal: 0,
  currentCombo: null,
  phase: null,
  phaseTotalIterations: 0,
  phaseDone: 0,
  phaseStartedAt: null,
  phasePass: null,
  overview: null,
  bestGlobal: null,
  startedAt: null,
  finishedDurations: [],
  interruptedReason: null,
};

// Routing key: matches the daemon queue's botFriendlyName (`${baseAsset}-${quoteAsset}`).
export const assetKey = (
  baseAsset: string | null | undefined,
  quoteAsset: string | null | undefined,
): string | null =>
  baseAsset && quoteAsset ? `${baseAsset}-${quoteAsset}` : null;

const assetKeyOf = (msg: CPCProgressMessage): string | null =>
  'baseAsset' in msg && 'quoteAsset' in msg
    ? assetKey(msg.baseAsset, msg.quoteAsset)
    : null;

// Per-asset reducer — applies a single run's progress transitions. This is the
// original single-run reducer, minus the Idle case (handled at the map level).
const runReducer = (
  state: ProgressState,
  msg: CPCProgressMessage,
): ProgressState => {
  // Discard messages for a stale run of this same asset (e.g. a late message
  // from the previous run arriving after the new RunStarted).
  if (
    'runId' in msg &&
    state.runId &&
    msg.type !== CPCLiveMessageType.RunStarted &&
    msg.runId !== state.runId
  ) {
    return state;
  }
  switch (msg.type) {
    case CPCLiveMessageType.RunStarted:
      return {
        ...EMPTY_PROGRESS,
        isIdle: false,
        isFinished: false,
        finishedDuration: null,
        runId: msg.runId,
        baseAsset: msg.baseAsset,
        quoteAsset: msg.quoteAsset,
        comboTotal: msg.numTimeFrameCombinations,
        startedAt: msg.startedAt,
      };
    case CPCLiveMessageType.ComboStarted:
      return {
        ...state,
        comboIndex: msg.comboIndex,
        comboTotal: msg.comboTotal,
        currentCombo: {
          higherTimeFrame: msg.higherTimeFrame,
          shorterTimeFrame: msg.shorterTimeFrame,
          multiOrderTimeFrame: msg.multiOrderTimeFrame,
          startedAt: msg.startedAt,
        },
        phase: null,
        phaseDone: 0,
        phaseTotalIterations: 0,
        phaseStartedAt: null,
      };
    case CPCLiveMessageType.PhaseStarted:
      return {
        ...state,
        phase: msg.phase,
        phaseTotalIterations: msg.totalIterations,
        phaseDone: 0,
        phaseStartedAt: Date.now(),
        phasePass: msg.pass ?? null,
      };
    case CPCLiveMessageType.BatchProgress: {
      const passChanged = state.phasePass !== (msg.pass ?? null);
      const phaseChanged = state.phase !== msg.phase || passChanged;
      return {
        ...state,
        phase: msg.phase,
        phaseDone: msg.done,
        phaseTotalIterations: msg.total,
        phaseStartedAt: phaseChanged ? Date.now() : state.phaseStartedAt,
        phasePass: msg.pass ?? state.phasePass,
        // Batches flowing again ⇒ the "no batch progress" interrupt is stale.
        interruptedReason: null,
      };
    }
    case CPCLiveMessageType.ComboFinished:
      return {
        ...state,
        // Advance the overall bar: this combo (0-based comboIndex) is done.
        comboIndex: msg.comboIndex + 1,
        comboTotal: msg.comboTotal,
        finishedDurations: [...state.finishedDurations, msg.duration],
        currentCombo: null,
        phase: null,
        phaseDone: 0,
        phaseTotalIterations: 0,
        phaseStartedAt: null,
        phasePass: null,
      };
    case CPCLiveMessageType.NewBestGlobal:
      return { ...state, bestGlobal: msg.bestResult };
    case CPCLiveMessageType.HeatmapSnapshot:
      return { ...state, overview: msg.overview };
    case CPCLiveMessageType.RunFinished:
      // Keep the final snapshot visible — don't fall back to idle. The
      // dashboard renders a "Run finished" banner while bestGlobal,
      // overview, etc. stay populated. Reset happens on the next RunStarted.
      return {
        ...state,
        isIdle: false,
        isFinished: true,
        finishedDuration: msg.duration,
        bestGlobal: msg.bestResult ?? state.bestGlobal,
        currentCombo: null,
        phase: null,
        phaseDone: 0,
        phaseTotalIterations: 0,
        phaseStartedAt: null,
        phasePass: null,
      };
    case CPCLiveMessageType.Interrupted:
      return { ...state, interruptedReason: msg.reason };
    default:
      return state;
  }
};

// Map of assetKey -> that asset's run state. With concurrent CPC runs every
// message carries its baseAsset/quoteAsset, so we route each into its own
// bucket; the UI then renders whichever asset the user has selected.
export type RunsByAsset = Record<string, ProgressState>;

const reducer = (state: RunsByAsset, msg: CPCProgressMessage): RunsByAsset => {
  // Idle means "no active runs on the server". Keep finished buckets around so
  // their results stay viewable; the consumer derives idle from the queue.
  if (msg.type === CPCLiveMessageType.Idle) return state;

  const key = assetKeyOf(msg);
  if (!key) return state;

  const prev = state[key];
  // A non-RunStarted message for an asset we have no bucket for — ignore. The
  // server replay always sends RunStarted first, so this is just defensive.
  if (!prev && msg.type !== CPCLiveMessageType.RunStarted) return state;

  const next = runReducer(prev ?? EMPTY_PROGRESS, msg);
  if (next === prev) return state;
  return { ...state, [key]: next };
};

const useCPCProgressWebsocket = () => {
  const url = ServicePaths.PROFIT_CALCULATIONS.Websocket;
  const [runsByAsset, dispatch] = useReducer(reducer, {});
  const { lastMessage, readyState, getWebSocket } = useWebsocket(url, {
    shouldReconnect: () => true,
    reconnectAttempts: Infinity,
    reconnectInterval: (attempt) => Math.min(30_000, 1000 * 2 ** attempt),
  });

  useEffect(() => {
    if (!lastMessage) return;
    try {
      const msg = JSON.parse(lastMessage.data) as CPCProgressMessage;
      dispatch(msg);
    } catch {
      // ignore
    }
  }, [lastMessage]);

  useEffect(() => {
    return () => {
      const ws = getWebSocket();
      if (ws && (ws.OPEN || ws.CONNECTING)) {
        ws.close();
      }
    };
  }, [getWebSocket]);

  return { runsByAsset, isConnected: readyState === 1 };
};

export default useCPCProgressWebsocket;
